import React from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { Clock, Calendar } from 'lucide-react';

function PeakHoursAnalysis({ data }) {
  if (!data) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 p-8 text-center">
        <Calendar className="w-12 h-12 text-slate-300 mx-auto mb-3" />
        <p className="text-slate-500 font-montserrat">No hay datos de horas pico</p>
      </div>
    );
  }

  const { hourly_distribution = [], daily_distribution = [], peak_hour = null, peak_day = null } = data;

  return (
    <div className="space-y-6">
      {/* Peak Info Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {peak_hour && (
          <div className="bg-gradient-to-r from-[#B1336E]/10 to-[#E6B43C]/10 rounded-xl p-6 border-l-4 border-[#B1336E]">
            <div className="flex items-center gap-3">
              <Clock className="w-8 h-8 text-[#B1336E]" />
              <div>
                <p className="text-xs text-slate-600 font-montserrat mb-1">Hora Pico</p>
                <p className="text-2xl font-bold text-[#B1336E] font-montserrat">
                  {peak_hour.hour}:00 hrs
                </p>
                <p className="text-sm text-slate-600 font-montserrat">
                  {peak_hour.count} entregas
                </p>
              </div>
            </div>
          </div>
        )}

        {peak_day && (
          <div className="bg-gradient-to-r from-[#67A43C]/10 to-[#67A43C]/5 rounded-xl p-6 border-l-4 border-[#67A43C]">
            <div className="flex items-center gap-3">
              <Calendar className="w-8 h-8 text-[#67A43C]" />
              <div>
                <p className="text-xs text-slate-600 font-montserrat mb-1">Día Pico</p>
                <p className="text-2xl font-bold text-[#67A43C] font-montserrat">
                  {peak_day.day_name}
                </p>
                <p className="text-sm text-slate-600 font-montserrat">
                  {peak_day.count} entregas
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Hourly Distribution Chart */}
      {hourly_distribution.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <h3 className="text-lg font-bold text-slate-900 font-montserrat mb-4 flex items-center gap-2">
            <Clock className="w-5 h-5 text-[#B1336E]" />
            Distribución por Hora del Día
          </h3>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={hourly_distribution}
                margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis 
                  dataKey="hour" 
                  tick={{ fill: '#64748b', fontSize: 12 }}
                  label={{ value: 'Hora del día', position: 'insideBottom', offset: -10 }}
                />
                <YAxis 
                  tick={{ fill: '#64748b', fontSize: 12 }}
                  label={{ value: 'Entregas', angle: -90, position: 'insideLeft' }}
                />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: '#fff',
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    fontFamily: 'Montserrat'
                  }}
                  labelFormatter={(value) => `${value}:00 hrs`}
                />
                <Bar 
                  dataKey="count" 
                  fill="#B1336E" 
                  name="Entregas"
                  radius={[8, 8, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Daily Distribution Chart */}
      {daily_distribution.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <h3 className="text-lg font-bold text-slate-900 font-montserrat mb-4 flex items-center gap-2">
            <Calendar className="w-5 h-5 text-[#B1336E]" />
            Distribución por Día de la Semana
          </h3>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={daily_distribution}
                margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis 
                  dataKey="day_name" 
                  tick={{ fill: '#64748b', fontSize: 12 }}
                />
                <YAxis 
                  tick={{ fill: '#64748b', fontSize: 12 }}
                  label={{ value: 'Entregas', angle: -90, position: 'insideLeft' }}
                />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: '#fff',
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    fontFamily: 'Montserrat'
                  }}
                />
                <Bar 
                  dataKey="count" 
                  fill="#67A43C" 
                  name="Entregas"
                  radius={[8, 8, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
    </div>
  );
}

export default PeakHoursAnalysis;